import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  browseArtists, getArtist, createBooking, listMyBookings,
  AGREEMENT_LABEL,
  type DirectoryArtist, type ArtistProfile, type AgreementRow,
} from '../../lib/organizer';
import { ApiError } from '../../lib/apiClient';
import { formatMoney, shortId } from '../../lib/format';
import { ReliabilityBadge } from '../../components/ReliabilityBadge';
import './OrganizerPages.css';

/**
 * Lineup + artist booking, on the event management page.
 *
 * Agency routing is deliberately invisible here. If the artist has a confirmed
 * agency the engine stamps it on the agreement and the agency answers on their
 * behalf — the organizer books the artist either way and the request they send
 * is byte-identical. Surfacing it would be leaking an implementation detail
 * into a screen that has no decision to make about it.
 */

/** Only these can still change; the rest are settled outcomes. */
const OPEN_STATUSES = new Set(['pending', 'signed', 'deposit_paid']);

function BookingForm({
  eventId, artist, onBooked, onCancel,
}: {
  eventId: string;
  artist: DirectoryArtist;
  onBooked: () => void;
  onCancel: () => void;
}) {
  const [profile, setProfile] = useState<ArtistProfile | null>(null);
  const [form, setForm] = useState({
    fee: '', deposit: '', startTime: '', duration: '', terms: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The list endpoint omits the bio; fetch the profile for the decision.
  useEffect(() => {
    let cancelled = false;
    getArtist(artist.artistId)
      .then((p) => { if (!cancelled) setProfile(p); })
      .catch(() => { /* the form still works without the extra detail */ });
    return () => { cancelled = true; };
  }, [artist.artistId]);

  const feeCents = Math.round(Number(form.fee) * 100);
  const depositCents = form.deposit === '' ? 0 : Math.round(Number(form.deposit) * 100);
  const depositTooBig = Number.isFinite(feeCents) && depositCents > feeCents;

  async function send() {
    if (!Number.isInteger(feeCents) || feeCents <= 0) {
      return setError('Enter a performance fee greater than zero.');
    }
    if (!Number.isInteger(depositCents) || depositCents < 0) {
      return setError('Enter a valid deposit.');
    }
    if (depositCents > feeCents) {
      return setError('The deposit cannot be more than the performance fee.');
    }

    setBusy(true);
    setError(null);
    try {
      const duration = Number.parseInt(form.duration, 10);
      await createBooking({
        eventId,
        artistProfileId: artist.artistId,
        performanceFeeCents: feeCents,
        depositCents,
        performanceStartTime: form.startTime ? `${form.startTime}:00` : null,
        setDurationMinutes: Number.isFinite(duration) ? duration : null,
        contractTerms: form.terms.trim() || null,
      });
      onBooked();
    } catch (err: unknown) {
      const code = err instanceof ApiError ? err.message : '';
      setError({
        not_your_event: 'You can only book artists for your own events.',
        artist_not_approved: 'That artist is not approved on Voleto yet.',
        invalid_fee: 'Enter a performance fee greater than zero.',
        invalid_deposit: 'The deposit must be between zero and the performance fee.',
      }[code] ?? code ?? 'Could not send that booking request.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="book-form">
      <header className="book-form__head">
        <div className="book-form__artist">
          <span className="lineup__avatar" aria-hidden="true">
            {artist.profileImageUrl
              ? <img src={artist.profileImageUrl} alt="" />
              : artist.stageName.slice(0, 1)}
          </span>
          <div>
            <h3 className="book-form__name">{artist.stageName}</h3>
            <p className="org-hint">
              {[artist.genre, artist.primaryMarket].filter(Boolean).join(' · ') || 'No genre listed'}
            </p>
          </div>
        </div>
        <ReliabilityBadge
          score={artist.reliabilityScore}
          buildingTrackRecord={artist.buildingTrackRecord}
          size="md"
        />
      </header>

      {profile?.bio && <p className="book-form__bio">{profile.bio}</p>}

      <div className="wiz-grid">
        <label className="field">
          <span className="field__label">Performance fee (USD) *</span>
          <input
            className="field__input mono" type="number" min="0" step="0.01" value={form.fee}
            placeholder="5000.00"
            onChange={(e) => setForm((f) => ({ ...f, fee: e.target.value }))}
          />
        </label>
        <label className="field">
          <span className="field__label">Deposit (USD)</span>
          <input
            className="field__input mono" type="number" min="0" step="0.01" value={form.deposit}
            placeholder="2500.00"
            onChange={(e) => setForm((f) => ({ ...f, deposit: e.target.value }))}
          />
          <span className="field__hint">
            Held until the show. Cannot exceed the fee.
          </span>
        </label>
        <label className="field">
          <span className="field__label">Set start time</span>
          <input
            className="field__input" type="time" value={form.startTime}
            onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
          />
        </label>
        <label className="field">
          <span className="field__label">Set length (minutes)</span>
          <input
            className="field__input mono" type="number" min="1" value={form.duration}
            placeholder="90"
            onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
          />
        </label>
        <label className="field wiz-field--wide">
          <span className="field__label">Contract terms</span>
          <textarea
            className="field__input wiz-textarea" rows={3} value={form.terms}
            placeholder="Backline, hospitality, travel, cancellation terms…"
            onChange={(e) => setForm((f) => ({ ...f, terms: e.target.value }))}
          />
        </label>
      </div>

      {depositTooBig && (
        <p className="org-blocked">
          <span aria-hidden="true">⚠</span> Deposit is larger than the fee.
        </p>
      )}
      {error && <p className="org-error" role="alert">{error}</p>}

      <div className="wiz-actions">
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button" className="button button--primary"
          disabled={busy || depositTooBig || !form.fee}
          onClick={send}
        >
          {busy ? 'Sending…' : 'Send booking request'}
        </button>
      </div>
    </div>
  );
}

export function EventLineup({ eventId }: { eventId: string }) {
  const [agreements, setAgreements] = useState<AgreementRow[] | null>(null);
  const [artists, setArtists] = useState<DirectoryArtist[] | null>(null);
  const [browsing, setBrowsing] = useState(false);
  const [selected, setSelected] = useState<DirectoryArtist | null>(null);
  const [genre, setGenre] = useState('');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadAgreements = useCallback(async () => {
    try {
      const all = await listMyBookings();
      // event_id is what makes this filter possible at all.
      setAgreements(all.filter((a) => a.event_id === eventId));
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not load the lineup.');
      setAgreements([]);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { void loadAgreements(); }, [loadAgreements]);

  const loadArtists = useCallback(async (g: string) => {
    try {
      setArtists(await browseArtists({ genre: g || undefined }));
    } catch {
      setArtists([]);
    }
  }, []);

  useEffect(() => {
    if (browsing) void loadArtists(genre);
  }, [browsing, genre, loadArtists]);

  // The engine filters by exact genre; free-text name search is client-side over
  // the returned page, which is bounded at 100.
  const visibleArtists = useMemo(() => {
    if (!artists) return null;
    const q = query.trim().toLowerCase();
    const booked = new Set(
      (agreements ?? []).filter((a) => OPEN_STATUSES.has(a.status)).map((a) => a.artist_profile_id),
    );
    return artists
      .filter((a) => !booked.has(a.artistId))
      .filter((a) => !q || a.stageName.toLowerCase().includes(q));
  }, [artists, query, agreements]);

  const open = (agreements ?? []).filter((a) => OPEN_STATUSES.has(a.status));
  const closed = (agreements ?? []).filter((a) => !OPEN_STATUSES.has(a.status));

  return (
    <section className="org-card">
      <header className="org-card__head">
        <div>
          <h2 className="org-card__title">Lineup</h2>
          <p className="org-hint">
            Booking requests and signed agreements for this event.
          </p>
        </div>
        {!browsing && !selected && (
          <button type="button" className="button button--primary"
            onClick={() => setBrowsing(true)}>
            + Book an artist
          </button>
        )}
      </header>

      {error && <p className="org-error" role="alert">{error}</p>}
      {loading && <p className="org-hint">Loading lineup…</p>}

      {/* ---------------------------------------------------- current lineup */}
      {!loading && agreements && agreements.length === 0 && !browsing && (
        <p className="org-hint">No artists booked yet.</p>
      )}

      {open.length > 0 && (
        <ul className="lineup-list">
          {open.map((a) => (
            <li key={a.id} className={`lineup-row lineup-row--${a.status}`}>
              <div className="lineup-row__artist">
                <span className="lineup__avatar" aria-hidden="true">
                  {a.profile_image_url
                    ? <img src={a.profile_image_url} alt="" />
                    : a.stage_name.slice(0, 1)}
                </span>
                <div>
                  <span className="wiz-tier__name">{a.stage_name}</span>
                  <span className="org-hint">
                    {a.genre ?? ' | '}
                    {a.performance_start_time ? ` · ${String(a.performance_start_time).slice(0, 5)}` : ''}
                    {a.set_duration_minutes ? ` · ${a.set_duration_minutes} min` : ''}
                  </span>
                </div>
              </div>

              <span className="lineup-row__fee mono">{formatMoney(a.performance_fee_cents)}</span>

              <div className="lineup-row__state">
                <span className={`org-pill org-pill--${a.status}`}>
                  {AGREEMENT_LABEL[a.status]}
                </span>
                <span className="org-hint mono" title={a.contract_number}>
                  {a.contract_number ?? shortId(a.id)}
                </span>
              </div>

              <div className="lineup-row__sigs org-hint">
                <span>{a.organizer_signed_at ? '✓ you signed' : 'you: unsigned'}</span>
                <span>{a.artist_signed_at ? '✓ artist signed' : 'artist: unsigned'}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {closed.length > 0 && (
        <details className="lineup-closed">
          <summary>
            {closed.length} closed {closed.length === 1 ? 'request' : 'requests'}
          </summary>
          <ul className="lineup-list">
            {closed.map((a) => (
              <li key={a.id} className="lineup-row lineup-row--closed">
                <span className="wiz-tier__name">{a.stage_name}</span>
                <span className="lineup-row__fee mono">{formatMoney(a.performance_fee_cents)}</span>
                <span className={`org-pill org-pill--${a.status}`}>
                  {AGREEMENT_LABEL[a.status]}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* --------------------------------------------------------- booking */}
      {selected && (
        <BookingForm
          eventId={eventId}
          artist={selected}
          onCancel={() => setSelected(null)}
          onBooked={() => {
            setSelected(null);
            setBrowsing(false);
            void loadAgreements();
          }}
        />
      )}

      {browsing && !selected && (
        <div className="artist-browser">
          <div className="adm-filters">
            <input
              className="field__input adm-search" type="search" placeholder="Search by name…"
              value={query} onChange={(e) => setQuery(e.target.value)}
            />
            <input
              className="field__input adm-search" type="text" placeholder="Genre (exact)"
              value={genre} onChange={(e) => setGenre(e.target.value)}
            />
            <button type="button" className="button button--ghost"
              onClick={() => { setBrowsing(false); setQuery(''); setGenre(''); }}>
              Close
            </button>
          </div>

          {visibleArtists === null && <p className="org-hint">Loading artists…</p>}
          {visibleArtists?.length === 0 && (
            <p className="org-hint">
              No approved artists match. Artists appear here once Voleto approves them.
            </p>
          )}

          <ul className="artist-grid">
            {visibleArtists?.map((a) => (
              <li key={a.artistId} className="artist-card">
                <span className="lineup__avatar" aria-hidden="true">
                  {a.profileImageUrl
                    ? <img src={a.profileImageUrl} alt="" />
                    : a.stageName.slice(0, 1)}
                </span>
                <div className="artist-card__body">
                  <span className="artist-card__name">{a.stageName}</span>
                  <span className="org-hint">
                    {[a.genre, a.primaryMarket].filter(Boolean).join(' · ') || '-'}
                  </span>
                  <ReliabilityBadge
                    score={a.reliabilityScore}
                    buildingTrackRecord={a.buildingTrackRecord}
                  />
                </div>
                <button type="button" className="button button--secondary artist-card__cta"
                  onClick={() => setSelected(a)}>
                  Book
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
